import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { contextDispatch, contextState } from '../utils/ContextProvider';
import getThemeClass from '../utils/getThemeClass';
import TextAlignment from './TextAlignment';
import renderContentfulBody from '../templates/embeddedEntries/richTextRenderer/renderContentfulBody';
import FullWidthImage from './FullWidthImage';
import Cutouts from './Cutouts';
import useWindowSize from '../utils/useWindowSize';
import styles from './Hero.module.css';

/** @param {FF.Hero} props */
function validateHeroProps({ contentfulId, cutouts, heroType }) {
  if (heroType === 'sign') {
    // have not solved the CSS conflict for this; same CSS logic for cutouts conflicts with CSS for sign
    if (
      cutouts?.find(({ orientation }) =>
        ['Left', 'Right'].includes(orientation),
      )
    ) {
      throw new Error(
        `Creating hero${
          contentfulId ? ` with contentful_id ${contentfulId}` : ''
        } failed; hero with type "Sign" cannot have a Left or Right Cutout`,
      );
    }
  }
}

/** @param {FF.Hero} props */
export default function Hero(props) {
  validateHeroProps(props);

  const {
    className = '',
    cutouts,
    heroText: {
      backgroundColor,
      textAlignment = 'Center',
      title,
      subtitle,
      caption,
      body,
      button,
    } = {},
    heroType,
    isVideo,
    media,
    withScrim,
  } = props;

  const { contextData } = contextState();
  const dispatch = contextDispatch();
  const { headerHeight = 86 } = useWindowSize() || {};

  // 32 = LinkButton height. We want to transition as soon as the header touches the button
  const headerSpacing = headerHeight + (button != null ? 32 : 0);

  const { ref, inView } = useInView({
    initialInView: true,
    rootMargin: `${-1 * headerSpacing}px 0px 0px 0px`,
  });

  useEffect(() => {
    dispatch({
      contextData: {
        ...contextData,
        showOpaqueHeader: !inView,
      },
      type: 'set',
    });
  }, [inView]);

  let heroWrapperClass =
    heroType === 'sign'
      ? `identifier--hero-wrapper-sign ${styles.heroWrapperSign}`
      : styles.heroWrapper;
  if (isVideo) {
    heroWrapperClass += ` ${styles.withVideo}`;
  }
  let copyWrapperClass =
    heroType === 'sign'
      ? `${styles.copyWrapperSign} ${getThemeClass(backgroundColor)}`
      : `${getThemeClass('Dark')}`;

  if (body) {
    copyWrapperClass += ` ${styles.copyWrapperRichText}`;
  }
  if (!withScrim && heroType === 'overlay') {
    copyWrapperClass += ` ${styles.copyWrapperTextShadow}`;
  }

  return (
    <Cutouts
      className={styles.cutoutsOffset}
      cutouts={cutouts || null}
      noPadding
    >
      <FullWidthImage
        className={`${heroWrapperClass} ${className}`}
        media={media}
        ref={button == null ? ref : null}
        withScrim={withScrim}
        /** set in CSS; needed for proper positioning */
        containerMaxHeight={810}
      >
        <TextAlignment
          alignment={
            textAlignment && heroType !== 'sign' ? textAlignment : 'Center'
          }
        >
          <div className={copyWrapperClass}>
            {/**
             * need to support "body" and individual fields, because /careers
             * possible it'd be nicer to just ask for JSX Element in props
             * */}
            {body &&
              renderContentfulBody(body, {
                'BLOCKS.EMBEDDED_ENTRY': { HeaderText: { allowH1: true } },
              })}
            {title && <h2 className={styles.copyWrapperTitle}>{title}</h2>}
            {subtitle && (
              <p className={styles.copyWrapperSubtitle}>{subtitle}</p>
            )}
            {caption && <p className={styles.copyWrapperCaption}>{caption}</p>}
            {button && (
              <div className={styles.linkWrapper} ref={ref}>
                {button}
              </div>
            )}
          </div>
        </TextAlignment>
      </FullWidthImage>
    </Cutouts>
  );
}
